/**
 * Global net for errors that escape React's render tree: uncaught runtime
 * errors and unhandled promise rejections. Both are forwarded to BetterStack
 * via the shared logger. React render errors are handled by ErrorBoundary and
 * query/mutation failures by the React Query cache handlers; this covers the
 * rest.
 *
 * Install once at app startup (see main.tsx). Safe to call more than once -
 * listeners are only attached on the first call.
 */

import { log, errorMessage } from '@/utils/logger';

let installed = false;

export function installGlobalErrorLogging(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    // Some cross-origin/opaque errors arrive with no message and no error
    // object; nothing useful to report, so skip rather than log "undefined".
    if (!event.message && event.error == null) return;
    // `error_*` keys, not `message` - Logtail reserves `message` for the log line.
    log.error('Uncaught error', {
      error_message: event.message || errorMessage(event.error),
      error_stack: event.error instanceof Error ? event.error.stack : undefined,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    log.error('Unhandled promise rejection', {
      error_message: errorMessage(event.reason),
      error_stack:
        event.reason instanceof Error ? event.reason.stack : undefined,
    });
  });
}
